import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Camera,
  Search,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  RefreshCw,
  Calendar,
  User,
  ChevronDown,
} from 'lucide-react';
import { useCompanies, useUpdateCompany, useAllContacts } from '@/hooks/useCompanies';
import { useSalesReps } from '@/hooks/useUsers';
import { calculateVisitStatus, getVisitStatusColor, formatDaysSinceVisit } from '@/services/visits';
import { updateCompany as updateCompanyService } from '@/services/companies';
import { useQueryClient } from '@tanstack/react-query';
import { companyKeys } from '@/hooks/useCompanies';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { companiesPageTour } from '@/walkthrough/tourSteps';
import type { Company, Contact, VisitStatus, CompanyTemperature, OutreachStatus } from '@/types';

type SortOption = 'name' | 'visitStatus' | 'lastVisit';

export function CompaniesPage() {
  const navigate = useNavigate();
  const { data: companies, isLoading, error, refetch } = useCompanies();
  const { data: allContacts, error: contactsError, isLoading: contactsLoading, refetch: refetchContacts } = useAllContacts();
  const { data: salesReps } = useSalesReps();
  const queryClient = useQueryClient();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(companiesPageTour);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [salesRepFilter, setSalesRepFilter] = useState('');
  const [visitStatusFilter, setVisitStatusFilter] = useState<VisitStatus | 'all'>('all');
  const [temperatureFilter, setTemperatureFilter] = useState<CompanyTemperature | 'all' | 'none'>('all');
  const [contactFilter, setContactFilter] = useState<OutreachStatus | 'all' | 'none'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('name');

  // Silent update handler for inline dropdowns (no toast)
  // Uses null for clearing values since updateCompany filters out undefined
  const handleUpdateField = async (companyId: string, field: string, value: any) => {
    try {
      await updateCompanyService(companyId, { [field]: value ?? null } as any);
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
    }
  };

  // Get unique company types for filter dropdown
  const companyTypes = useMemo(() => {
    if (!companies) return [];
    const types = new Set(companies.map((c) => c.type).filter(Boolean));
    return Array.from(types).sort();
  }, [companies]);

  // Index contacts by companyId for fast cross-company search lookup
  const contactsByCompany = useMemo(() => {
    const map = new Map<string, Contact[]>();
    if (!allContacts) return map;
    for (const contact of allContacts) {
      const list = map.get(contact.companyId);
      if (list) list.push(contact);
      else map.set(contact.companyId, [contact]);
    }
    return map;
  }, [allContacts]);

  // Helper to get visit status for a company
  const getCompanyVisitStatus = (company: Company) => {
    return calculateVisitStatus(company.lastVisitDate || null);
  };

  // Filter and sort companies
  const filteredCompanies = useMemo(() => {
    if (!companies) return [];

    const searchLower = searchQuery.trim().toLowerCase();

    // First filter
    const filtered = companies.filter((company) => {
      let matchesSearch = !searchLower;
      if (!matchesSearch) {
        matchesSearch =
          company.name.toLowerCase().includes(searchLower) ||
          (company.email?.toLowerCase().includes(searchLower) ?? false) ||
          (company.phone?.toLowerCase().includes(searchLower) ?? false) ||
          (company.city?.toLowerCase().includes(searchLower) ?? false) ||
          (company.primaryContactName?.toLowerCase().includes(searchLower) ?? false);
      }
      if (!matchesSearch) {
        const companyContacts = contactsByCompany.get(company.id);
        if (companyContacts) {
          matchesSearch = companyContacts.some((c) => {
            const fullName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim().toLowerCase();
            return (
              fullName.includes(searchLower) ||
              c.firstName?.toLowerCase().includes(searchLower) ||
              c.lastName?.toLowerCase().includes(searchLower) ||
              c.email?.toLowerCase().includes(searchLower) ||
              c.phone?.toLowerCase().includes(searchLower) ||
              c.mobile?.toLowerCase().includes(searchLower) ||
              c.title?.toLowerCase().includes(searchLower)
            );
          });
        }
      }

      const matchesType = !typeFilter || company.type === typeFilter;

      const matchesSalesRep = !salesRepFilter || company.salesRepUserId === salesRepFilter;

      // Filter by visit status
      let matchesVisitStatus = true;
      if (visitStatusFilter !== 'all') {
        const { status } = getCompanyVisitStatus(company);
        matchesVisitStatus = status === visitStatusFilter;
      }

      const matchesTemperature = temperatureFilter === 'all' ||
        (temperatureFilter === 'none' ? !company.temperature : company.temperature === temperatureFilter);

      const matchesContact = contactFilter === 'all' ||
        (contactFilter === 'none' ? !company.outreachStatus : company.outreachStatus === contactFilter);

      // Hide inactive unless toggled
      const matchesActive = showInactive || !company.isInactive;

      return matchesSearch && matchesType && matchesSalesRep && matchesVisitStatus && matchesTemperature && matchesContact && matchesActive;
    });

    // Then sort
    return filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }

      if (sortBy === 'visitStatus') {
        // Sort by status: red first (needs attention), then yellow, then green
        const statusOrder: Record<VisitStatus, number> = { red: 0, yellow: 1, green: 2 };
        const statusA = getCompanyVisitStatus(a).status;
        const statusB = getCompanyVisitStatus(b).status;
        return statusOrder[statusA] - statusOrder[statusB];
      }

      if (sortBy === 'lastVisit') {
        // Sort by last visit date, oldest first (needs attention)
        const dateA = a.lastVisitDate ? new Date(a.lastVisitDate).getTime() : 0;
        const dateB = b.lastVisitDate ? new Date(b.lastVisitDate).getTime() : 0;
        return dateA - dateB; // Oldest first
      }

      return 0;
    });
  }, [companies, contactsByCompany, searchQuery, typeFilter, salesRepFilter, visitStatusFilter, temperatureFilter, contactFilter, showInactive, sortBy]);

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Failed to load companies</p>
          <button onClick={() => refetch()} className="btn btn-primary">
            <RefreshCw size={20} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">Referral Sources</h1>
          <p className="page-description hidden sm:block">
            Company directory for referral partnerships. Visible to all team members.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Link to="/import/business-cards" className="btn btn-secondary flex items-center gap-1.5 text-sm py-2 px-3">
            <Camera size={16} />
            <span>Import</span>
          </Link>
          <Link to="/companies/new" data-tour="add-company" className="btn btn-primary flex items-center gap-1.5 text-sm py-2 px-3">
            <Plus size={16} />
            <span>Add Company</span>
          </Link>
        </div>
      </div>

      {/* Contact-search availability banner: surface CG-query failure or in-flight load */}
      {contactsError ? (
        <div className="card bg-red-50 border border-red-200">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-red-700">
              <span className="font-medium">Contact search unavailable.</span>{' '}
              Company-name search still works; typing a contact's name won't surface their company until this loads.
              Open DevTools → Console for the underlying error.
            </div>
            <button
              onClick={() => refetchContacts()}
              className="btn btn-secondary text-xs py-1 px-2 flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Retry
            </button>
          </div>
        </div>
      ) : contactsLoading ? (
        <div className="card bg-blue-50 border border-blue-200">
          <p className="text-xs text-blue-700">Loading contacts for cross-company search…</p>
        </div>
      ) : null}

      {/* Search and filters */}
      <div className="card space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div data-tour="company-search" className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search company, contact name, email, phone, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input sm:w-48"
          >
            <option value="">All Types</option>
            {companyTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Sales Rep, Visit Status Filter and Sort */}
        <div data-tour="company-filters" className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Sales Rep:</span>
            <select
              value={salesRepFilter}
              onChange={(e) => setSalesRepFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="">All Reps</option>
              {salesReps?.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Visit Status:</span>
            <select
              value={visitStatusFilter}
              onChange={(e) => setVisitStatusFilter(e.target.value as VisitStatus | 'all')}
              className="input w-auto"
            >
              <option value="all">All</option>
              <option value="red">🔴 Needs Visit</option>
              <option value="yellow">🟡 Visit Soon</option>
              <option value="green">🟢 Recent</option>
            </select>
          </div>

          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Temperature:</span>
            <select
              value={temperatureFilter}
              onChange={(e) => setTemperatureFilter(e.target.value as CompanyTemperature | 'all' | 'none')}
              className="input w-auto"
            >
              <option value="all">All</option>
              <option value="engaged">Engaged</option>
              <option value="considering">Considering</option>
              <option value="not_interested">Not Interested</option>
              <option value="none">No Temp</option>
            </select>
          </div>

          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Contact:</span>
            <select
              value={contactFilter}
              onChange={(e) => setContactFilter(e.target.value as OutreachStatus | 'all' | 'none')}
              className="input w-auto"
            >
              <option value="all">All</option>
              <option value="working_on_it">Working on it</option>
              <option value="call_back">Call back</option>
              <option value="email">Email</option>
              <option value="text">Text</option>
              <option value="none">No Status</option>
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">Show Inactive</span>
          </label>

          <div data-tour="company-sort" className="flex gap-2 items-center">
            <span className="text-sm text-gray-600 whitespace-nowrap">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="input w-auto"
            >
              <option value="name">Name (A-Z)</option>
              <option value="visitStatus">Visit Status (Needs Attention First)</option>
              <option value="lastVisit">Last Visit (Oldest First)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Companies list */}
      {isLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        </div>
      ) : filteredCompanies.length > 0 ? (
        <div data-tour="company-cards" className="space-y-3">
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onClick={() => navigate(`/companies/${company.id}`)}
              onUpdateField={handleUpdateField}
            />
          ))}
        </div>
      ) : companies && companies.length > 0 ? (
        <div className="card">
          <div className="empty-state">
            <Search size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No matching companies</p>
            <p className="mt-1">Try adjusting your search or filters.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Building2 size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">No referral sources yet</p>
            <p className="mt-1 mb-4">
              Get started by adding your first referral partner company.
            </p>
            <Link to="/companies/new" className="btn btn-primary">
              <Plus size={20} />
              Add First Company
            </Link>
          </div>
        </div>
      )}

      {/* Stats footer */}
      {companies && companies.length > 0 && (
        <div data-tour="company-stats-footer" className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {filteredCompanies.length} of {companies.length} companies
          </span>
          <button
            onClick={() => {
              setSearchQuery('');
              setTypeFilter('');
              setSalesRepFilter('');
              setVisitStatusFilter('all');
              setTemperatureFilter('all');
              setContactFilter('all');
              setSortBy('name');
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        </div>
      )}

      <WelcomeModal
        pageName={companiesPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Temperature & Status Helpers
// ===========================================

const TEMPERATURE_OPTIONS: { value: CompanyTemperature | ''; label: string; bg: string; text: string }[] = [
  { value: '', label: 'No Temp', bg: 'bg-gray-200', text: 'text-gray-600' },
  { value: 'engaged', label: 'Engaged', bg: 'bg-emerald-500', text: 'text-white' },
  { value: 'considering', label: 'Considering', bg: 'bg-amber-400', text: 'text-white' },
  { value: 'not_interested', label: 'Not Interested', bg: 'bg-red-500', text: 'text-white' },
];

const STATUS_OPTIONS: { value: OutreachStatus | ''; label: string; bg: string; text: string }[] = [
  { value: '', label: 'No Status', bg: 'bg-gray-200', text: 'text-gray-600' },
  { value: 'working_on_it', label: 'Working on it', bg: 'bg-orange-500', text: 'text-white' },
  { value: 'call_back', label: 'Call back', bg: 'bg-purple-500', text: 'text-white' },
  { value: 'email', label: 'Email', bg: 'bg-blue-500', text: 'text-white' },
  { value: 'text', label: 'Text', bg: 'bg-sky-400', text: 'text-white' },
];

function getTempOption(temp?: CompanyTemperature) {
  return TEMPERATURE_OPTIONS.find((o) => o.value === (temp || '')) || TEMPERATURE_OPTIONS[0];
}

function getStatusOption(status?: OutreachStatus) {
  return STATUS_OPTIONS.find((o) => o.value === (status || '')) || STATUS_OPTIONS[0];
}

// Monday.com-style color pill dropdown
function ColorPillDropdown<T extends string>({
  value,
  options,
  onChange,
  title,
}: {
  value: T | '' | undefined;
  options: { value: T | ''; label: string; bg: string; text: string }[];
  onChange: (val: T | '') => void;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const current = options.find((o) => o.value === (value || '')) || options[0];

  return (
    <div ref={ref} className="relative" title={title}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${current.bg} ${current.text} text-xs font-semibold rounded px-2.5 py-1 min-w-[90px] text-center whitespace-nowrap flex items-center justify-center gap-1 hover:opacity-90 transition-opacity`}
      >
        {current.label}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 right-0 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[130px]">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value as T | '');
                setOpen(false);
              }}
              className="w-full px-2 py-1.5 flex items-center gap-2 hover:bg-gray-50"
            >
              <span className={`${opt.bg} ${opt.text} text-xs font-semibold rounded px-2 py-0.5 flex-1 text-center`}>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================
// Company Card Component
// ===========================================

interface CompanyCardProps {
  company: Company;
  onClick: () => void;
  onUpdateField: (id: string, field: string, value: any) => void;
}

function CompanyCard({ company, onClick, onUpdateField }: CompanyCardProps) {
  // Calculate visit status
  const { status, daysSince } = calculateVisitStatus(company.lastVisitDate || null);
  const colors = getVisitStatusColor(status);

  const cardBg = company.isInactive ? 'bg-gray-50' : company.commissionType ? 'bg-green-50' : 'bg-white';
  const inactiveStyle = company.isInactive ? 'opacity-50' : '';

  return (
    <div
      onClick={onClick}
      className={`w-full text-left ${cardBg} ${inactiveStyle} rounded-lg shadow hover:shadow-md transition-shadow p-3 sm:p-4 flex items-center gap-3 cursor-pointer`}
    >
      {/* Visit Status Indicator */}
      <div className="flex-shrink-0 relative">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-50 flex items-center justify-center">
          <Building2 className="text-blue-600" size={20} />
        </div>
        {/* Traffic light dot */}
        <div
          className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${colors.dot}`}
          title={`${status === 'green' ? 'Recently visited' : status === 'yellow' ? 'Visit soon' : 'Needs visit'}`}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 mr-auto" style={{ flex: '1 1 40%', maxWidth: '55%' }}>
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900 truncate">{company.name}</h3>
          {company.isInactive && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 font-medium">Inactive</span>
          )}
          {company.type && (
            <span className="badge badge-blue">{company.type}</span>
          )}
        </div>

        {/* Primary Contact Name */}
        {company.primaryContactName && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
            <User size={12} />
            <span>{company.primaryContactName}</span>
          </div>
        )}

        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
          {/* Last Visit Info */}
          <span className={`flex items-center gap-1 ${colors.text}`}>
            <Calendar size={14} />
            {formatDaysSinceVisit(daysSince)}
          </span>
          {company.phone && (
            <span className="flex items-center gap-1">
              <Phone size={14} />
              {company.phone}
            </span>
          )}
          {company.email && (
            <span className="flex items-center gap-1">
              <Mail size={14} />
              {company.email}
            </span>
          )}
          {(company.city || company.state) && (
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {[company.city, company.state].filter(Boolean).join(', ')}
            </span>
          )}
        </div>
      </div>

      {/* Inline Temp & Contact Pill Dropdowns */}
      <div className="flex-shrink-0 flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <ColorPillDropdown<CompanyTemperature>
          value={company.temperature}
          options={TEMPERATURE_OPTIONS}
          onChange={(val) => onUpdateField(company.id, 'temperature', val || null)}
          title="Temperature - Interest level"
        />
        <ColorPillDropdown<OutreachStatus>
          value={company.outreachStatus}
          options={STATUS_OPTIONS}
          onChange={(val) => onUpdateField(company.id, 'outreachStatus', val || null)}
          title="Contact Status (automations not created yet)"
        />
      </div>

      {/* Arrow */}
      <ChevronRight className="flex-shrink-0 text-gray-400" size={20} />
    </div>
  );
}
